import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Campaign as CampaignIcon,
  ChevronRight as ChevronRightIcon,
  HelpOutline as HelpOutlineIcon,
  InfoOutlined as InfoOutlinedIcon,
  MailOutline as MailOutlineIcon,
  FiberManualRecord as FiberManualRecordIcon,
  PostAdd as PostAddIcon,
} from '@mui/icons-material';
import { BusinessGridCommon } from '../../../../../Components/BusinessGridCommon';
import { getColumnDefs1 } from './RiskAssessment_ColumnDefs';
import WorkerOpinionModal from './modals/WorkerOpinionModal';
import WorkerOpinionReceptionPeriodModal from './modals/WorkerOpinionReceptionPeriodModal';
import WorkerOpinionReceptionEndModal from './modals/WorkerOpinionReceptionEndModal';
import TodaySafetySelectModal from './modals/TodaySafetySelectModal';
import { WAIT_STEP_STATUS } from '../Step00_Wait/Wait_Schema';
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './RiskAssessment_GridOverrides.css';

const HEADER_GREEN = '#2CA900';
const ACCENT_GREEN = '#2CA900';
const HEADER_GREEN_DARK = '#217F00';
/** 근로자 의견 필 버튼 — 참고 UI(연한 배경 + 진한 테두리) */
const WORKER_OPINION_PILL_BASE = {
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '0.8125rem',
  letterSpacing: '-0.01em',
  borderRadius: 999,
  px: 2.5,
  py: 0.85,
  minHeight: 36,
  borderWidth: 1.5,
  borderStyle: 'solid',
  boxShadow: 'none',
  '& .MuiButton-startIcon': { mr: 0.65, ml: -0.2 },
};
const WORKER_OPINION_PILL_MINT = {
  ...WORKER_OPINION_PILL_BASE,
  bgcolor: 'rgba(236, 253, 245, 0.96)',
  borderColor: HEADER_GREEN,
  color: HEADER_GREEN_DARK,
  '&:hover': {
    bgcolor: '#ECFDF5',
    borderColor: HEADER_GREEN_DARK,
    color: HEADER_GREEN_DARK,
  },
};
const WORKER_OPINION_PILL_COLLECTING = {
  ...WORKER_OPINION_PILL_BASE,
  bgcolor: '#FFFCF6',
  borderColor: '#E8B84D',
  color: '#B45309',
  '&:hover': {
    bgcolor: '#FFF8EB',
    borderColor: '#D97706',
    color: '#9A3412',
  },
};
const CARD_BORDER = 'rgba(0,0,0,0.08)';
const STEPPER_RAIL_TOP_PX = 16;
/** 이 화면 단계 — 스텝 ID 매칭 실패 시 폴백 인덱스 */
const CURRENT_STEP_INDEX = 1;
const CURRENT_STEP_ID = 'riskAssessment';

/** 근로자 의견 받기 툴팁 제목 */
const WORKER_OPINION_RECEPTION_TOOLTIP_TITLE = (
  <Box sx={{ maxWidth: 300, py: 0.25 }}>
    <Stack
      direction="row"
      alignItems="flex-start"
      spacing={0.75}
      sx={{ mb: 0.75 }}
    >
      <HelpOutlineIcon
        sx={{ fontSize: 22, color: '#fff', flexShrink: 0, mt: '1px' }}
      />
      <Typography
        component="span"
        sx={{
          fontWeight: 800,
          fontSize: '1rem',
          lineHeight: 1.35,
          color: '#fff',
        }}
      >
        왜 하나요?
      </Typography>
    </Stack>
    <Typography
      component="span"
      sx={{
        display: 'block',
        fontSize: '0.875rem',
        fontWeight: 600,
        lineHeight: 1.5,
        color: 'rgba(255,255,255,0.96)',
        pl: 0.25,
      }}
    >
      산업안전보건법 제36조에 따른 근로자 참여 의무
    </Typography>
  </Box>
);

/**
 * 위험성평가 실시 화면단 (기획 UI: 상단 스텝퍼 + 좌측 공정 목록 + 우측 세부 그리드)
 */
function RiskAssessment({
  assessmentTitle = '',
  rsMethod = '',
  /** GetRisCustom01R1 RiskMaster.MatrixList — 빈도·강도 위험성 배지 LevelNm 구간 */
  riskMatrixList = [],
  workflowSteps,
  processList = [],
  selectedSriId,
  onSelectProcess,
  detailRows = [],
  onPatchRow,
  onRequestPhoto,
  onSelectedDetailKeysChange,
  onAddRow,
  onDeleteRows,
  onTempSave,
  onSaveAndNext,
  onCancel,
  onRefresh,
  onImport,
  onExport,
  onApplyWorkerOpinion,
  /** GetHomeData 등 — TB_BS_CUST 근로자의견 수렴 기간·IS_RISK=Y (Drawer와 동일 플래그) */
  riskWorkerOpinionMenuActive = false,
  workerOpinionReceptionActive = false,
  workerOpinionReceptionSaveLoading = false,
  onWorkerOpinionReceptionStart,
  onWorkerOpinionReceptionEnd,
  /** RIS_BASIC_01 표준모델 요인(GetRisBasic01R6) — 공정 선택 후 세부 그리드에 반영 */
  onOpenStandardModel,
  loading = false,
  /** ACS_BASIC_01과 동일 GridCheckBox 연동 (selectedRowNosRef) */
  detailGridRef: detailGridRefProp,
  selectedRowNosRef,
  lastCheckedIndexRef,
  DeleteNewRow,
  RestoreRow,
  handleRowSelection,
}) {
  const rsMethodCaption = useMemo(() => {
    const norm = String(rsMethod ?? '')
      .replace(/[·ㆍ]/g, '')
      .replace(/\s+/g, '')
      .trim();
    if (!norm) return '';
    return norm.includes('빈도강도')
      ? '빈도·강도법'
      : norm.includes('3단계') || norm.includes('단계판단')
        ? '위험성 수준 3단계 판단법'
        : String(rsMethod);
  }, [rsMethod]);

  const [workerOpinionOpen, setWorkerOpinionOpen] = useState(false);
  const [
    workerOpinionReceptionPeriodOpen,
    setWorkerOpinionReceptionPeriodOpen,
  ] = useState(false);
  const [workerOpinionReceptionEndOpen, setWorkerOpinionReceptionEndOpen] =
    useState(false);
  const [todaySafetyModalOpen, setTodaySafetyModalOpen] = useState(false);
  const [todaySafetyOptionRows, setTodaySafetyOptionRows] = useState([]);
  const [todaySafetyTarget, setTodaySafetyTarget] = useState({
    clientKey: '',
    value: '',
  });
  const internalDetailGridRef = useRef(null);
  const detailGridRef = detailGridRefProp ?? internalDetailGridRef;

  /** 좌측 패널: 부서 목록(초기) -> 공정 목록(부서 선택 후) */
  const [leftPanelMode, setLeftPanelMode] = useState('dept'); // 'dept' | 'process'
  const [selectedDeptNm, setSelectedDeptNm] = useState('');

  const currentSelectedProcess = useMemo(
    () => processList.find((p) => p.sriId === selectedSriId) ?? null,
    [processList, selectedSriId]
  );
  const currentSelectedDeptNm = currentSelectedProcess?.depNm ?? '';

  const processByDept = useMemo(() => {
    const m = new Map();
    for (const p of processList ?? []) {
      const dep = String(p?.depNm ?? '').trim() || '미지정';
      if (!m.has(dep)) m.set(dep, []);
      m.get(dep).push(p);
    }
    return m;
  }, [processList]);

  const departmentList = useMemo(() => {
    return Array.from(processByDept.entries()).map(([depNm, list]) => ({
      depNm,
      count: list?.length ?? 0,
    }));
  }, [processByDept]);

  const filteredProcessList = useMemo(() => {
    if (leftPanelMode !== 'process') return [];
    const dep = selectedDeptNm || currentSelectedDeptNm || '';
    return (dep && processByDept.get(dep)) || [];
  }, [leftPanelMode, selectedDeptNm, currentSelectedDeptNm, processByDept]);

  useEffect(() => {
    if (leftPanelMode !== 'dept') return;
    if (selectedDeptNm) return;
    if (currentSelectedDeptNm) {
      setSelectedDeptNm(currentSelectedDeptNm);
      return;
    }
    if (departmentList.length > 0) {
      setSelectedDeptNm(departmentList[0].depNm);
    }
  }, [leftPanelMode, selectedDeptNm, currentSelectedDeptNm, departmentList]);

  const HandleSelectDept = useCallback(
    (depNm) => {
      setSelectedDeptNm(depNm);
      setLeftPanelMode('process');
      const list = processByDept.get(depNm) ?? [];
      const first = list?.[0];
      if (first?.sriId != null) onSelectProcess?.(first.sriId);
    },
    [processByDept, onSelectProcess]
  );

  const HandleBackToDept = useCallback(() => {
    setLeftPanelMode('dept');
  }, []);

  const HandleSelectProcessFromLeft = useCallback(
    (p) => {
      if (!p) return;
      const dep = String(p?.depNm ?? '').trim() || '미지정';
      setSelectedDeptNm(dep);
      setLeftPanelMode('process');
      onSelectProcess?.(p.sriId);
    },
    [onSelectProcess]
  );
  const isFrequencyIntensityMethod = useMemo(
    () =>
      String(rsMethod ?? '')
        .replace(/[·ㆍ]/g, '')
        .replace(/\s+/g, '')
        .trim() === '빈도강도법',
    [rsMethod]
  );

  /** 세션에서 시작했거나, 홈·사업장 플래그로 수렴 기간 중이면 수집/수립 UI */
  const workerOpinionReceptionPillActive = useMemo(
    () => workerOpinionReceptionActive || riskWorkerOpinionMenuActive,
    [workerOpinionReceptionActive, riskWorkerOpinionMenuActive]
  );
  const workerOpinionReceptionPillLabel = useMemo(() => {
    if (workerOpinionReceptionActive) return '의견 수집 중';
    if (riskWorkerOpinionMenuActive) return '의견 수립 중';
    return '근로자 의견 받기';
  }, [workerOpinionReceptionActive, riskWorkerOpinionMenuActive]);

  const columnDefsDetail = useMemo(
    () =>
      getColumnDefs1({
        gridRef: detailGridRef,
        rowData: detailRows,
        rsMethod,
        riskMatrixList,
        onRequestPhoto,
        selectedRowNosRef,
        lastCheckedIndexRef,
        DeleteNewRow,
        RestoreRow,
        handleRowSelection,
      }),
    [
      detailRows,
      rsMethod,
      riskMatrixList,
      onRequestPhoto,
      detailGridRef,
      selectedRowNosRef,
      lastCheckedIndexRef,
      DeleteNewRow,
      RestoreRow,
      handleRowSelection,
    ]
  );

  const detailExtraRowClassRules = useMemo(
    () => ({
      'worker-opinion-row': (params) =>
        String(params?.data?.workerOpinionRowYn ?? '')
          .trim()
          .toUpperCase() === 'Y',
    }),
    []
  );

  const HandleDetailCellValueChanged = useCallback(
    (e) => {
      const field = e.colDef?.field;
      if (!field || !e.data?.clientKey) return;
      // 날짜 컬럼은 valueSetter가 정규화한 e.data[field]를 사용 (agDateCellEditor의 newValue가 빈값으로 들어오는 케이스 방지)
      if (field === 'gaesunExDate' || field === 'gaesunComDate') {
        const toYmd = (v) => {
          if (v == null) return null;
          if (v instanceof Date && !Number.isNaN(v.getTime())) {
            return v.toISOString().slice(0, 10);
          }
          const s = String(v).trim();
          if (!s) return null;
          return s.slice(0, 10);
        };

        // 우선순위: valueSetter가 써둔 data[field] → newValue/value(에디터 반환) → oldValue 유지
        const fromData = toYmd(e.data?.[field]);
        const fromEvent = toYmd(
          e.newValue !== undefined ? e.newValue : e.value
        );
        const fromOld = toYmd(e.oldValue);

        const next = fromData || fromEvent || fromOld || null;
        onPatchRow?.(e.data.clientKey, field, next);
        return;
      }

      const v = e.newValue !== undefined ? e.newValue : e.value;
      onPatchRow?.(e.data.clientKey, field, v);
    },
    [onPatchRow]
  );

  const HandleDetailCellEditingStopped = useCallback(
    (e) => {
      const field = e.colDef?.field;
      if (!field || !e.data?.clientKey) return;
      // 편집 종료 시점 강제 동기화 필드:
      // - 날짜: onCellValueChanged 누락/빈값 이벤트 보정
      // - agSelectCellEditor: 선택 후 blur 시 valueChanged 누락 보정
      const forceSyncFields = new Set([
        'gaesunExDate',
        'gaesunComDate',
        'todayPossibility',
        'todayImportance',
        'afterPossibility',
        'afterImportance',
      ]);
      if (forceSyncFields.has(field)) {
        const v = e.data?.[field] ?? null;
        onPatchRow?.(e.data.clientKey, field, v);
      }
    },
    [onPatchRow]
  );

  const HandleDetailSelectionChanged = useCallback(
    (gridRef) => {
      const api = gridRef?.current?.api;
      if (!api || !onSelectedDetailKeysChange) return;
      const rows = api.getSelectedRows?.() ?? [];
      const keys = rows.map((r) => r.no).filter((k) => k != null);
      if (selectedRowNosRef?.current) {
        selectedRowNosRef.current = new Set(keys);
      }
      onSelectedDetailKeysChange(keys);
    },
    [onSelectedDetailKeysChange, selectedRowNosRef]
  );

  const HandleDetailCellClicked = useCallback(
    (e) => {
      const gridApi = detailGridRef?.current?.api;
      if (!gridApi) return;
      const isCheckboxClick = e.event?.target?.type === 'checkbox';
      if (isCheckboxClick) return;

      if (e.colDef?.field === 'reductionMeasures') {
        const optionsRaw = Array.isArray(e.data?.reductionMeasuresOptions)
          ? e.data.reductionMeasuresOptions
          : [];
        const options = optionsRaw
          .map((x) => String(x ?? '').trim())
          .filter((x) => x.length > 0);
        const clientKey = String(e.data?.clientKey ?? '').trim();
        if (clientKey) {
          // 팝업을 열 때마다 기존 값을 초기화하여 누적 입력을 방지
          onPatchRow?.(clientKey, 'reductionMeasures', '');
        }
        setTodaySafetyTarget({
          clientKey,
          value: '',
          hazardSituation: String(e.data?.categoryComment ?? '').trim(),
        });
        setTodaySafetyOptionRows(options);
        setTodaySafetyModalOpen(true);
        return;
      }

      gridApi.ensureIndexVisible(e.rowIndex, 'middle');
      const copied = JSON.parse(JSON.stringify(e.data));
      handleRowSelection?.(copied, e.rowIndex);
      gridApi.getDisplayedRowAtIndex(e.rowIndex)?.setSelected?.(true, false);
    },
    [detailGridRef, handleRowSelection, onPatchRow]
  );

  const HandleConfirmTodaySafety = useCallback(
    (values) => {
      const clientKey = String(todaySafetyTarget?.clientKey ?? '').trim();
      if (!clientKey) return;
      const list = Array.isArray(values) ? values : [values];
      const text = list
        .map((x) => String(x ?? '').trim())
        .filter((x) => x.length > 0)
        .map((x) => x.replace(/^(\d+\.\s*)+/, '').trim())
        .filter((x) => x.length > 0)
        .map((x, idx) => `${idx + 1}. ${x}`)
        .join('\n');
      onPatchRow?.(clientKey, 'reductionMeasures', text);
      setTodaySafetyModalOpen(false);
    },
    [onPatchRow, todaySafetyTarget]
  );

  useEffect(() => {
    detailGridRef.current?.api?.deselectAll?.();
    selectedRowNosRef?.current?.clear?.();
    if (lastCheckedIndexRef) lastCheckedIndexRef.current = null;
  }, [selectedSriId, selectedRowNosRef, lastCheckedIndexRef, detailGridRef]);

  const HandleToolbarRefresh = useCallback(async () => {
    await onRefresh?.();
    requestAnimationFrame(() => {
      const api = detailGridRef.current?.api;
      if (!api) return;
      api.refreshCells({ force: true });
      api.redrawRows();
    });
  }, [onRefresh, detailGridRef]);

  // 빈도·강도법/3단계 판단법 모두 동일한 행 높이 유지
  const GetDetailRowHeight = useCallback(() => 88, []);

  // 상단 스텝퍼 목록 가져오기
  const stepList = Array.isArray(workflowSteps) ? workflowSteps : [];
  const currentStepIndexFromId = stepList.findIndex(
    (s) => (s?.routeStepId || s?.stepId) === CURRENT_STEP_ID
  );
  const currentStepIndex =
    currentStepIndexFromId >= 0
      ? currentStepIndexFromId
      : stepList.length > 0
        ? Math.min(CURRENT_STEP_INDEX, stepList.length - 1)
        : -1;

  const currentProcessNm =
    processList.find((p) => p.sriId === selectedSriId)?.processNm ?? '';

  const depNmForGridHeader = String(currentSelectedDeptNm || '').trim();

  const nextStepTitle = stepList[currentStepIndex + 1]?.title ?? '다음 단계';

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: 'calc(100dvh - 88px)',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f0f4f0',
        boxSizing: 'border-box',
      }}
    >
      {loading ? (
        <LinearProgress
          sx={{ position: 'sticky', top: 0, zIndex: 20, height: 3 }}
        />
      ) : null}

      <Paper
        elevation={0}
        square
        sx={{
          bgcolor: HEADER_GREEN,
          color: '#fff',
          px: { xs: 1.5, sm: 2 },
          py: 1.25,
          borderRadius: 0,
          flexShrink: 0,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
            <IconButton
              size="small"
              onClick={onCancel}
              sx={{ color: '#fff' }}
              aria-label="닫기"
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="subtitle1" fontWeight={800} noWrap>
              위험성평가 실시
            </Typography>
            {assessmentTitle ? (
              <Typography variant="caption" sx={{ opacity: 0.9 }} noWrap>
                · {assessmentTitle}
              </Typography>
            ) : null}
            {rsMethodCaption ? (
              <Typography variant="caption" sx={{ opacity: 0.9 }} noWrap>
                · {rsMethodCaption}
              </Typography>
            ) : null}
          </Stack>
          <Button
            size="small"
            variant="outlined"
            startIcon={<SaveIcon sx={{ fontSize: 18 }} />}
            onClick={onTempSave}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.55)',
              flexShrink: 0,
              '&:hover': {
                borderColor: '#fff',
                bgcolor: 'rgba(255,255,255,0.08)',
              },
            }}
          >
            임시저장
          </Button>
        </Stack>
      </Paper>

      {/* 스텝퍼 — API(GetRisCustom00R1)에서만 스텝 수·순서·제목 수신 */}
      {stepList.length > 0 ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 0,
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: { xs: 1.5, sm: 3 },
            pt: 1.5,
            pb: 1.25,
            flexShrink: 0,
            overflowX: 'auto',
            bgcolor: '#FAFBFC',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              minWidth: { xs: 640, sm: 'auto' },
            }}
          >
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                left: { xs: '5%', sm: '6%' },
                right: { xs: '5%', sm: '6%' },
                top: STEPPER_RAIL_TOP_PX,
                height: 2,
                bgcolor: '#D6DCE4',
                borderRadius: 1,
                zIndex: 0,
              }}
            />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{ position: 'relative', zIndex: 1, width: '100%' }}
            >
              {stepList.map((step, index) => {
                const isActive =
                  currentStepIndex >= 0 && index === currentStepIndex;
                const isDone =
                  step?.status === WAIT_STEP_STATUS.completed && !isActive;
                const isPast =
                  isDone || (currentStepIndex >= 0 && index < currentStepIndex);
                return (
                  <Stack
                    key={String(step.stepId ?? step.routeStepId ?? index)}
                    alignItems="center"
                    sx={{
                      flex: '1 1 0',
                      minWidth: 0,
                      px: { xs: 0.25, sm: 0.5 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        flexShrink: 0,
                        bgcolor: isActive
                          ? '#0F9D00'
                          : isPast
                            ? '#DDF3D7'
                            : '#F7F9FC',
                        color: isActive || isPast ? '#fff' : '#667085',
                        border: isActive
                          ? '2px solid #0A6B00'
                          : isPast
                            ? '2px solid #4FA73D'
                            : '2px solid #C6CED8',
                        boxShadow: isActive
                          ? '0 3px 12px rgba(15, 157, 0, 0.50)'
                          : isPast
                            ? '0 1px 6px rgba(79, 167, 61, 0.30)'
                            : '0 1px 2px rgba(0,0,0,0.05)',
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Typography
                      variant="caption"
                      align="center"
                      sx={{
                        mt: 0.6,
                        fontWeight: isActive ? 800 : isPast ? 700 : 500,
                        color: isActive
                          ? '#0A6B00'
                          : isPast
                            ? '#267A1A'
                            : '#98A2B3',
                        lineHeight: 1.35,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        width: '100%',
                        maxWidth: 108,
                      }}
                    >
                      {step.title}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        </Paper>
      ) : null}

      <Box
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          p: { xs: 1.5, sm: 2 },
          gap: 2,
          overflow: 'hidden',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: { xs: '100%', md: 220 },
            flexShrink: 0,
            borderRadius: 2,
            border: '1px solid',
            borderColor: '#E6E8EB',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: { xs: 220, md: 'none' },
            bgcolor: '#FFFFFF',
            p: 0.75,
          }}
        >
          <Box
            sx={{
              px: 0.9,
              py: 0.6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderRadius: 1.5,
              bgcolor: '#F8F9FB',
            }}
          >
            {leftPanelMode === 'dept' ? (
              <>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: '#6B7280',
                    letterSpacing: '-0.01em',
                    fontSize: { xs: '0.86rem', sm: '0.95rem' },
                  }}
                >
                  부서 목록
                </Typography>
                <Chip
                  label={`${departmentList.length}개`}
                  size="small"
                  sx={{
                    height: 22,
                    bgcolor: '#F3F4F6',
                    color: '#6B7280',
                    border: '1px solid #E5E7EB',
                    '& .MuiChip-label': {
                      px: 0.9,
                      fontSize: '0.76rem',
                      fontWeight: 700,
                    },
                  }}
                />
              </>
            ) : (
              <>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <IconButton
                    size="small"
                    onClick={HandleBackToDept}
                    aria-label="부서로 돌아가기"
                    sx={{ p: 0.25 }}
                  >
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 800,
                      color: '#6B7280',
                      letterSpacing: '-0.01em',
                      fontSize: { xs: '0.86rem', sm: '0.95rem' },
                    }}
                  >
                    공정 목록
                  </Typography>
                </Stack>
                <Chip
                  label={`${filteredProcessList.length}건`}
                  size="small"
                  sx={{
                    height: 22,
                    bgcolor: '#F3F4F6',
                    color: '#6B7280',
                    border: '1px solid #E5E7EB',
                    '& .MuiChip-label': {
                      px: 0.9,
                      fontSize: '0.76rem',
                      fontWeight: 700,
                    },
                  }}
                />
              </>
            )}
          </Box>
          <List
            dense
            disablePadding
            sx={{ overflow: 'auto', flex: 1, px: 0.5, pb: 0.5, pt: 0.5 }}
          >
            {leftPanelMode === 'dept' ? (
              departmentList.map((d) => {
                const sel = d.depNm === selectedDeptNm;
                return (
                  <ListItemButton
                    key={d.depNm}
                    selected={sel}
                    onClick={() => HandleSelectDept(d.depNm)}
                    sx={{
                      minHeight: 40,
                      px: 1,
                      py: 0.45,
                      borderRadius: 1.2,
                      mb: 0.35,
                      border: sel
                        ? `1px solid ${ACCENT_GREEN}`
                        : '1px solid transparent',
                      bgcolor: sel ? 'rgba(44, 169, 0, 0.12)' : 'transparent',
                      '&:hover': {
                        bgcolor: sel
                          ? 'rgba(44, 169, 0, 0.16)'
                          : 'rgba(0,0,0,0.02)',
                      },
                    }}
                  >
                    <ListItemText
                      primary={d.depNm || '미지정'}
                      secondary={`${d.count}건 공정`}
                      primaryTypographyProps={{
                        fontSize: { xs: '0.88rem', sm: '0.95rem' },
                        lineHeight: 1.35,
                        color: sel ? ACCENT_GREEN : '#374151',
                        fontWeight: sel ? 800 : 700,
                        noWrap: true,
                      }}
                      secondaryTypographyProps={{
                        fontSize: '0.78rem',
                        color: '#6B7280',
                        noWrap: true,
                      }}
                    />
                    {sel ? (
                      <ChevronRightIcon
                        fontSize="small"
                        sx={{ color: ACCENT_GREEN, fontSize: 16, ml: 0.25 }}
                      />
                    ) : null}
                  </ListItemButton>
                );
              })
            ) : (
              <>
                {filteredProcessList.map((p) => {
                  const sel = p.sriId === selectedSriId;
                  return (
                    <ListItemButton
                      key={p.sriId || p.processNm}
                      selected={sel}
                      onClick={() => HandleSelectProcessFromLeft(p)}
                      sx={{
                        minHeight: 40,
                        px: 1,
                        py: 0.45,
                        borderRadius: 1.2,
                        mb: 0.35,
                        border: sel
                          ? `1px solid ${ACCENT_GREEN}`
                          : '1px solid transparent',
                        bgcolor: sel ? 'rgba(44, 169, 0, 0.12)' : 'transparent',
                        '&:hover': {
                          bgcolor: sel
                            ? 'rgba(44, 169, 0, 0.16)'
                            : 'rgba(0,0,0,0.02)',
                        },
                      }}
                    >
                      <ListItemText
                        primary={p.processNm || '공정'}
                        primaryTypographyProps={{
                          fontSize: { xs: '0.88rem', sm: '0.95rem' },
                          lineHeight: 1.35,
                          color: sel ? ACCENT_GREEN : '#374151',
                          fontWeight: sel ? 800 : 700,
                          noWrap: true,
                        }}
                      />
                      {sel ? (
                        <ChevronRightIcon
                          fontSize="small"
                          sx={{
                            color: ACCENT_GREEN,
                            fontSize: 16,
                            ml: 0.25,
                          }}
                        />
                      ) : null}
                    </ListItemButton>
                  );
                })}
                {!filteredProcessList.length ? (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      선택한 부서의 공정이 없습니다.
                    </Typography>
                  </Box>
                ) : null}
              </>
            )}

            {leftPanelMode === 'dept' && !departmentList.length ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  등록된 부서가 없습니다.
                </Typography>
              </Box>
            ) : null}
          </List>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            borderRadius: 2,
            border: '1px solid',
            borderColor: CARD_BORDER,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: '#fff',
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.25,
              borderBottom: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1.5}
              sx={{ width: '100%' }}
            >
              {depNmForGridHeader ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.25}
                  sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.9}
                    sx={{
                      minWidth: 0,
                      maxWidth: { xs: '48%', sm: '44%' },
                      flexShrink: 1,
                      bgcolor: 'rgba(44, 169, 0, 0.09)',
                      border: '1px solid rgba(44, 169, 0, 0.28)',
                      borderRadius: 2,
                      px: 1.25,
                      py: 0.5,
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.65)',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        flexShrink: 0,
                        fontWeight: 800,
                        color: HEADER_GREEN,
                        letterSpacing: '0.04em',
                        fontSize: '0.68rem',
                        lineHeight: 1,
                      }}
                    >
                      부서
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={800}
                      noWrap
                      sx={{
                        minWidth: 0,
                        color: HEADER_GREEN_DARK,
                        letterSpacing: '-0.02em',
                        lineHeight: 1.35,
                      }}
                    >
                      {depNmForGridHeader}
                    </Typography>
                  </Stack>
                  <ChevronRightIcon
                    sx={{
                      fontSize: 22,
                      color: 'rgba(15, 23, 42, 0.22)',
                      flexShrink: 0,
                    }}
                  />
                  <Stack
                    spacing={0.2}
                    sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: 'text.secondary',
                        lineHeight: 1.2,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      공정
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      fontWeight={800}
                      noWrap
                      sx={{
                        color: 'text.primary',
                        letterSpacing: '-0.03em',
                        lineHeight: 1.25,
                      }}
                    >
                      {currentProcessNm || '공정'}
                    </Typography>
                  </Stack>
                </Stack>
              ) : (
                <Typography
                  variant="subtitle1"
                  fontWeight={800}
                  noWrap
                  sx={{
                    minWidth: 0,
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    letterSpacing: '-0.02em',
                  }}
                >
                  {currentProcessNm || '공정'}
                </Typography>
              )}
              <Typography
                variant="caption"
                sx={{
                  textAlign: 'right',
                  flexShrink: 0,
                  ml: 'auto',
                  color: '#D32F2F',
                  fontWeight: 800,
                }}
              >
                * 엑셀 양식을 다운로드하여 작성 후 업로드하시면, 평가 내용이
                공정 별로 일괄 반영됩니다.
              </Typography>
            </Stack>
          </Box>

          <Box
            className="ag-theme-alpine"
            sx={{
              flex: '1 1 auto',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              px: 1,
              pb: 1,
            }}
          >
            <BusinessGridCommon
              gridRef={detailGridRef}
              getRowId={(p) => String(p.data?.clientKey ?? '')}
              columnDefs={columnDefsDetail}
              extraRowClassRules={detailExtraRowClassRules}
              rowData={detailRows}
              setSelectedRowData={() => {}}
              setChangedRowData={() => {}}
              editable
              title1="위험성평가 세부항목"
              searchYn1="N"
              copyYn1="N"
              addYn1="Y"
              saveYn1="N"
              deleteYn1="Y"
              printYn1="N"
              fileUploadYn1="Y"
              fileDownloadYn1="Y"
              refreshYn1="Y"
              addClick1={onAddRow}
              deleteClick1={onDeleteRows}
              refreshClick1={HandleToolbarRefresh}
              fileUploadClick1={onImport}
              fileDownloadClick1={onExport}
              other1={
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  sx={{ ml: 1 }}
                >
                  <Tooltip
                    title={WORKER_OPINION_RECEPTION_TOOLTIP_TITLE}
                    arrow
                    placement="top"
                    enterDelay={150}
                    slotProps={{
                      tooltip: {
                        sx: {
                          bgcolor: HEADER_GREEN,
                          color: '#fff',
                          border: `2px solid ${HEADER_GREEN_DARK}`,
                          borderRadius: 1.5,
                          px: 1.75,
                          py: 1.25,
                          boxShadow:
                            '0 6px 20px rgba(34, 127, 0, 0.38), 0 2px 8px rgba(0,0,0,0.12)',
                          '& .MuiTooltip-arrow': {
                            color: HEADER_GREEN,
                          },
                        },
                      },
                      popper: {
                        modifiers: [
                          { name: 'offset', options: { offset: [0, 10] } },
                        ],
                      },
                    }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      disableElevation
                      startIcon={
                        workerOpinionReceptionPillActive ? (
                          <FiberManualRecordIcon
                            sx={{
                              fontSize: 12,
                              color: HEADER_GREEN,
                            }}
                          />
                        ) : (
                          <MailOutlineIcon
                            sx={{ fontSize: 20, color: HEADER_GREEN }}
                          />
                        )
                      }
                      onClick={() => {
                        if (workerOpinionReceptionPillActive) {
                          setWorkerOpinionReceptionEndOpen(true);
                        } else {
                          setWorkerOpinionReceptionPeriodOpen(true);
                        }
                      }}
                      sx={
                        workerOpinionReceptionPillActive
                          ? WORKER_OPINION_PILL_COLLECTING
                          : WORKER_OPINION_PILL_MINT
                      }
                    >
                      {workerOpinionReceptionPillLabel}
                    </Button>
                  </Tooltip>
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    disableElevation
                    startIcon={
                      <CampaignIcon
                        sx={{ fontSize: 20, color: HEADER_GREEN }}
                      />
                    }
                    onClick={() => setWorkerOpinionOpen(true)}
                    sx={WORKER_OPINION_PILL_MINT}
                  >
                    근로자 의견 청취
                  </Button>
                  {onOpenStandardModel ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      disableElevation
                      startIcon={
                        <PostAddIcon
                          sx={{ fontSize: 20, color: HEADER_GREEN }}
                        />
                      }
                      onClick={() => onOpenStandardModel()}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '0.8125rem',
                        borderRadius: 999,
                        px: 2,
                        py: 0.85,
                        minHeight: 36,
                        borderWidth: 1.5,
                        borderColor: HEADER_GREEN,
                        color: HEADER_GREEN_DARK,
                        '&:hover': {
                          bgcolor: 'rgba(44, 169, 0, 0.08)',
                          borderColor: HEADER_GREEN_DARK,
                        },
                      }}
                    >
                      표준모델 요인 추가
                    </Button>
                  ) : null}
                </Stack>
              }
              rowSelection="multiple"
              onSelectionChanged={HandleDetailSelectionChanged}
              onCellClicked={HandleDetailCellClicked}
              onCellValueChanged={HandleDetailCellValueChanged}
              onCellEditingStopped={HandleDetailCellEditingStopped}
              getRowHeight={GetDetailRowHeight}
              gridSx={{
                flex: 1,
                minHeight: 360,
                height: '100%',
                p: 1,
                pt: 0,
                '--ag-row-height': '88px',
              }}
              autoHeaderHeight
              pagination
              stopEditingWhenCellsLoseFocus={true}
            />
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              px: 2,
              pt: 0.25,
              pb: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16, color: '#b26a00' }} />
            <Typography
              variant="body2"
              sx={{ color: '#8a4b00', fontWeight: 600 }}
            >
              근로자 의견 청취를 반영한 행은 연한 주황색 배경으로 표시됩니다.
            </Typography>
          </Box>
          {isFrequencyIntensityMethod ? (
            <Box
              sx={{
                flexShrink: 0,
                px: 2,
                pb: 1.25,
                pt: 0.25,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
              }}
            ></Box>
          ) : (
            <Box
              sx={{
                flexShrink: 0,
                px: 2,
                pb: 1.25,
                pt: 0.25,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
              }}
            ></Box>
          )}
        </Paper>
      </Box>
      <WorkerOpinionModal
        open={workerOpinionOpen}
        onClose={() => setWorkerOpinionOpen(false)}
        onApplySelection={onApplyWorkerOpinion}
      />
      <WorkerOpinionReceptionPeriodModal
        open={workerOpinionReceptionPeriodOpen}
        onClose={() => setWorkerOpinionReceptionPeriodOpen(false)}
        loading={workerOpinionReceptionSaveLoading}
        onSubmit={onWorkerOpinionReceptionStart}
      />
      <WorkerOpinionReceptionEndModal
        open={workerOpinionReceptionEndOpen}
        onClose={() => setWorkerOpinionReceptionEndOpen(false)}
        loading={workerOpinionReceptionSaveLoading}
        onConfirm={onWorkerOpinionReceptionEnd}
      />
      <TodaySafetySelectModal
        open={todaySafetyModalOpen}
        options={todaySafetyOptionRows}
        selectedValue={todaySafetyTarget?.value}
        hazardSituation={todaySafetyTarget?.hazardSituation}
        title="위험성 감소대책 선택"
        manualPlaceholder="위험성 감소대책을 직접 입력하세요."
        onClose={() => setTodaySafetyModalOpen(false)}
        onConfirm={HandleConfirmTodaySafety}
      />

      {/* 하단 푸터 — SafetyInfo와 동일 패턴 */}
      <Paper
        elevation={0}
        square
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          px: { xs: 2, sm: 3 },
          py: 1.5,
          flexShrink: 0,
          bgcolor: '#fff',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onCancel}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              alignSelf: { xs: 'stretch', sm: 'auto' },
            }}
          >
            이전 단계
          </Button>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ sm: 'center' }}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              다음 단계: {nextStepTitle}
            </Typography>
            <Button
              variant="contained"
              onClick={onSaveAndNext}
              endIcon={<ChevronRightIcon />}
              fullWidth={false}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                px: 3,
                bgcolor: ACCENT_GREEN,
                '&:hover': { bgcolor: HEADER_GREEN_DARK },
                alignSelf: { xs: 'stretch', sm: 'auto' },
              }}
            >
              저장 후 다음
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}

export default RiskAssessment;
